import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

// Refused fullscreen requests in this browser tab. One refusal can be a fluke
// (the browser didn't credit the tap as a user gesture) and is worth a retry;
// after a few the browser clearly won't allow fullscreen — stop asking. Kept
// in sessionStorage so a page reload doesn't start nagging again: a browser
// that got stuck refusing keeps doing so until the tab is closed.
const REFUSALS_KEY = "snugfit.fullscreenRefusals";
const MAX_REFUSALS = 2;
// a request neither granted nor refused in this long counts as refused (some
// mobile browsers leave the promise pending).
const REQUEST_TIMEOUT_MS = 2000;
// wait this long after fullscreen is left before asking for it again.
const EXIT_SETTLE_MS = 300;

// let go of the orientation lock; it only makes sense while fullscreen, and a
// stale one may keep the next fullscreen request from ever being answered.
function unlockOrientation() {
  try {
    window.screen.orientation.unlock();
  } catch {
    // unsupported or nothing locked.
  }
}

function getRefusals() {
  try {
    return Number(sessionStorage.getItem(REFUSALS_KEY)) || 0;
  } catch {
    return 0;
  }
}

function setRefusals(count) {
  try {
    sessionStorage.setItem(REFUSALS_KEY, String(count));
  } catch {
    // storage unavailable — the count just won't survive a reload.
  }
}

// whether it's still worth showing the prompt.
function shouldAsk() {
  return !document.fullscreenElement && getRefusals() < MAX_REFUSALS;
}

// Keeps the app in portrait by entering fullscreen and locking orientation.
// The prompt has no way to dismiss it: the user has to go full screen.
//
// screen.orientation.lock() is only honored when the page is fullscreen (or
// installed as a PWA). On devices without fullscreen/lock support (e.g. iPhone)
// or with a fine pointer (desktop) the dialog never shows.
export const PortraitLock = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // fullscreenEnabled is false when fullscreen is blocked outright (e.g.
    // inside an app's in-app browser) — requests could never succeed there.
    const supported =
      window.matchMedia("(pointer: coarse)").matches &&
      Boolean(document.documentElement.requestFullscreen) &&
      document.fullscreenEnabled !== false &&
      Boolean(window.screen?.orientation?.lock);
    if (!supported) {
      return undefined;
    }

    // installed as a PWA: the manifest already locks portrait, but apply the
    // lock anyway in case the manifest is ignored; no fullscreen needed.
    if (window.matchMedia("(display-mode: standalone)").matches) {
      try {
        window.screen.orientation.lock("portrait").catch((err) => {
          console.warn(
            "[PortraitLock] orientation lock failed:",
            err?.name,
            err?.message,
          );
        });
      } catch {
        // unsupported — nothing else we can do.
      }
      return undefined;
    }

    // ask at the start, and ask again whenever fullscreen is exited — unless
    // the browser keeps refusing.
    const update = () => setOpen(shouldAsk());
    update();
    let retryTimer;
    const onChange = () => {
      const fullscreen = Boolean(document.fullscreenElement);
      clearTimeout(retryTimer);
      if (fullscreen) {
        update();
        return;
      }
      // fullscreen was left: drop the old orientation lock, and give the exit
      // a moment to settle before asking again.
      unlockOrientation();
      retryTimer = setTimeout(update, EXIT_SETTLE_MS);
    };
    const onError = (e) => {
      console.error("[PortraitLock] fullscreenerror event", e.target);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("fullscreenerror", onError);
    return () => {
      clearTimeout(retryTimer);
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("fullscreenerror", onError);
    };
  }, []);

  const enterFullscreen = async () => {
    setOpen(false);
    const started = Date.now();
    try {
      await Promise.race([
        document.documentElement.requestFullscreen(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("timeout")), REQUEST_TIMEOUT_MS),
        ),
      ]);
      setRefusals(0);
      window.screen.orientation.lock("portrait").catch(() => {});
    } catch (err) {
      console.error(
        `[PortraitLock] fullscreen refused after ${Date.now() - started}ms:`,
        err?.name,
        err?.message,
      );
      // the request was refused.
      // Re-open the dialog so the next tap retries with a fresh user
      // gesture; once the browser clearly won't allow it, stay quiet (the
      // app still works, just unlocked).
      setRefusals(getRefusals() + 1);
      setOpen(shouldAsk());
    }
  };

  return (
    <Dialog open={open} sx={{ zIndex: 2000 }}>
      <DialogTitle>Switch to full screen?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Full screen is needed to lock the screen from rotating.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={enterFullscreen}>
          Go full screen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
